import React, { useMemo, useState } from "react";
import { gql } from "@apollo/client";
import {
  useHrisEmployeeSidebarQuery,
  useHrisEmployeeRemoteHrisDataQuery,
} from "./HrisEmployeeDetailSidebar.generated";
import { useQueryParam, createEnumParam, withDefault } from "use-query-params";
import SidebarHeader, {
  SidebarContent,
  SidebarTitle,
} from "@src/features/sidebars/ui/SidebarHeader";
import InfoTable, { InfoTableItem } from "@src/lib/ui/InfoTable";
import CommaSeparatedLinks from "@src/features/sidebars/ui/CommaSeparatedLinks";
import SidebarLink from "@src/features/sidebars/ui/SidebarLink";
import ClickableLinkText from "@src/lib/ui/ClickableLinkText";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import { format, parseISO } from "date-fns";
import Text from "@src/lib/ui/Text";
import Link from "next/link";
import { cn } from "@src/lib/utils/cn";
import PaginatedTable from "@src/lib/ui/PaginatedTable";
import {
  useMergeBackfillRunsLazyQuery,
  MergeBackfillRunFragment,
  useMergeBackfillRunsQuery,
} from "./HrisSyncsListContainer.generated";
import {
  useHrisEmployeeEventsLazyQuery,
  HrisEmployeeEventFragment,
  useHrisEmployeeEventsQuery,
} from "./HrisEmployeeDetailSidebar.generated";
import {
  GraphqlOperations,
  MergeBackfillRunsInput,
  HrisEmployeeEventsInput,
} from "@src/types.generated";

import {
  QueryTabs,
  QueryTabRenderFns,
  QueryTabCopy,
  QueryTabCounts,
} from "@src/lib/ui/QueryTabs";
import { useToast } from "@src/lib/hooks/useToast";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import LinkHrisEmployeeDialog from "@src/features/settings/dialogs/LinkHrisEmployeeDialog";
import { DeleteHrisEmployeeDialog } from "./DeleteHrisEmployeeDialog";
import { useDeleteHrisEmployeesMutation } from "./HrisEmployeeListContainer.generated";
import { Button } from "@src/lib/ui/Button";
import LinkIcon from "@src/lib/icons/18px/link";
import UserOutlineIcon from "@src/lib/icons/18px/user-outline";
import ArrowsRefreshIcon from "@src/lib/icons/18px/arrows-refresh";
import DotsIcon from "@src/lib/icons/18px/dots";
import TrashOutlineIcon from "@src/lib/icons/18px/trash-outline";
import { getJsonDiffSummary } from "@src/lib/utils/jsonDiff";
import StyledJsonDiff from "@src/lib/ui/StyledJsonDiff";
import FormattedJson from "@src/lib/ui/FormattedJson";
import DataNotAvailableMessage from "@src/lib/ui/DataNotAvailableMessage";
import { useHris } from "@src/features/settings/hooks/useHris";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";

type Props = {
  rowId: number;
  showHeader?: boolean;
};

const HrisEmployeeSidebar: React.FC<Props> = ({ rowId, showHeader = true }) => {
  const { hasRegularHrisIntegration, hasMergeBackfillRuns } = useHris();
  const [linkEmployeeOpen, setLinkEmployeeOpen] = useState(false);
  const [deleteEmployeeOpen, setDeleteEmployeeOpen] = useState(false);
  const { addToast } = useToast();
  const { userActionPermissions } = useActionPermissions();
  const [deleteHrisEmployees] = useDeleteHrisEmployeesMutation({
    refetchQueries: [GraphqlOperations.Query.HrisEmployees],
    onCompleted: () => {
      addToast({ message: "HRIS employee deleted successfully" });
    },
  });

  const { data } = useHrisEmployeeSidebarQuery({
    variables: {
      id: rowId,
    },
    skip: !rowId,
  });

  // Track which tabs are active
  const [mainTab] = useQueryParam(
    "hris-employee-tab",
    withDefault(
      createEnumParam(["details", "syncs", "rawData", "history"]),
      "details",
    ),
  );
  const [rawDataSubTab] = useQueryParam(
    "hris-employee-raw-data-tab",
    withDefault(
      createEnumParam(["fromLastSync", "currentlyInRemoteHris", "diff"]),
      "fromLastSync",
    ),
  );

  // Only fetch remoteHrisDataPassthrough when viewing the relevant tabs
  const shouldFetchRemoteHrisData =
    mainTab === "rawData" &&
    (rawDataSubTab === "currentlyInRemoteHris" || rawDataSubTab === "diff");

  // Separate query for remoteHrisDataPassthrough - only fetched when needed
  const {
    data: remoteHrisData,
    loading: remoteHrisDataLoading,
    error: remoteHrisDataError,
  } = useHrisEmployeeRemoteHrisDataQuery({
    variables: {
      id: rowId,
    },
    skip: !rowId || !shouldFetchRemoteHrisData,
  });

  const inputFilters: MergeBackfillRunsInput = useMemo(() => {
    return {
      filters: {
        hrisEmployeeId: { value: rowId },
      },
    };
  }, [rowId]);

  const historyInputFilters: HrisEmployeeEventsInput = useMemo(() => {
    return {
      filters: {
        hrisEmployeeId: { value: rowId },
      },
    };
  }, [rowId]);

  // Fetch total count for the tab badge
  const { data: syncsCountData } = useMergeBackfillRunsQuery({
    variables: {
      input: inputFilters,
      pagination: { limit: 0 }, // Only need the count, not the data
    },
    skip: !rowId,
  });

  const { data: historyCountData } = useHrisEmployeeEventsQuery({
    variables: {
      input: historyInputFilters,
      pagination: { limit: 0 }, // Only need the count, not the data
    },
    skip: !rowId,
  });

  // Fetch all events for diff computation (we'll use a larger limit)
  const { data: allHistoryData } = useHrisEmployeeEventsQuery({
    variables: {
      input: historyInputFilters,
      pagination: { limit: 1000 }, // Get enough events to compute diffs
    },
    skip: !rowId,
  });

  // Compute diff summaries for all events
  const diffSummaries = useMemo(() => {
    if (!allHistoryData?.HrisEmployeeEvents?.objects) return new Map();

    const events = [...allHistoryData.HrisEmployeeEvents.objects].sort(
      (a, b) => {
        const timeA = a.pghCreatedAt ? new Date(a.pghCreatedAt).getTime() : 0;
        const timeB = b.pghCreatedAt ? new Date(b.pghCreatedAt).getTime() : 0;
        return timeB - timeA; // Descending order (newest first)
      },
    );

    const summaries = new Map<number, string | null>();

    for (let i = 0; i < events.length; i++) {
      const current = events[i];
      const previous = i < events.length - 1 ? events[i + 1] : null;

      const summary = getJsonDiffSummary(
        current.rawHrisData,
        previous?.rawHrisData,
      );

      summaries.set(current.pghId, summary);
    }

    return summaries;
  }, [allHistoryData]);

  const columns = ["id", "queuedAt", "completedAt"] as const;
  type Column = (typeof columns)[number];

  const historyColumns = [
    "pghId",
    "pghCreatedAt",
    "diffSummary",
    "pghContextActorId",
    "pghContextActorName",
    "pghContextCorrelationId",
    "pghContextIsHris",
    "pghContextReason",
  ] as const;
  type HistoryColumn = (typeof historyColumns)[number];

  const employee = data?.HrisEmployee;

  const tabKeys = useMemo(() => {
    const keys: Array<"details" | "rawData" | "syncs" | "history"> = [
      "details",
      "rawData",
    ];
    // Show syncs tab if there are MergeBackfillRuns OR if org has non-SFTP integration
    if (hasMergeBackfillRuns || hasRegularHrisIntegration) {
      keys.push("syncs");
    }
    keys.push("history");
    return keys;
  }, [hasMergeBackfillRuns, hasRegularHrisIntegration]);

  const tabCounts: QueryTabCounts<"syncs" | "rawData" | "history"> =
    useMemo(() => {
      return {
        syncs: syncsCountData?.MergeBackfillRuns?.totalCount ?? undefined,
        history: historyCountData?.HrisEmployeeEvents?.totalCount ?? undefined,
      };
    }, [syncsCountData, historyCountData]);

  if (!employee) return null;

  type TabKey = (typeof tabKeys)[number];

  const tabLabels: QueryTabCopy<TabKey> = {
    details: "Details",
    syncs: "Modified by syncs",
    rawData: "Remote HRIS data",
    history: "History",
  };

  const tabQueryTabRenderFns: QueryTabRenderFns<TabKey> = {
    details: () => <InfoTable items={items} />,
    syncs: () => (
      <PaginatedTable<
        MergeBackfillRunFragment,
        Column,
        typeof useMergeBackfillRunsQuery,
        typeof useMergeBackfillRunsLazyQuery,
        Column
      >
        key="employee-detail-sidebar-syncs-table"
        defaultSort={{ column: "queuedAt", descending: true }}
        queryHook={useMergeBackfillRunsQuery}
        lazyQueryHook={useMergeBackfillRunsLazyQuery}
        getItems={(x) => x?.MergeBackfillRuns?.objects ?? []}
        getTotalItemCount={(x) => x?.MergeBackfillRuns?.totalCount ?? 0}
        itemId={(x) => x.id}
        input={inputFilters}
        sidebarConfig={{
          tableId: "hris-employee-syncs",
          sidebarType: DetailSidebarType.HrisSync,
          enabled: true,
        }}
        allColumns={[...columns]}
        columnSelection={{
          enabled: true,
          alwaysVisibleColumns: ["id"],
          defaultSelectedColumns: [...columns],
        }}
        columnTitles={{
          id: "Sync ID",
          queuedAt: "Queued at",
          completedAt: "Completed at",
        }}
        renderFns={{
          id: (x) => (
            <div className="flex flex-1 items-center gap-2">
              <ArrowsRefreshIcon className="text-gray-7" />
              <Text
                variant="compact-title"
                ellipsis
                ellipsisParentFlexDirection={"row"}
              >
                {x.id}
              </Text>
            </div>
          ),
          queuedAt: (x) =>
            x.queuedAt ? format(parseISO(x.queuedAt), "M/d/yyyy h:mm a") : "-",
          completedAt: (x) =>
            x.completedAt
              ? format(parseISO(x.completedAt), "M/d/yyyy h:mm a")
              : "-",
        }}
      />
    ),
    rawData: () => {
      const rawDataTabKeys = [
        "fromLastSync",
        "currentlyInRemoteHris",
        "diff",
      ] as const;
      type RawDataTabKey = (typeof rawDataTabKeys)[number];

      const rawDataTabLabels: QueryTabCopy<RawDataTabKey> = {
        fromLastSync: "Last synced data",
        currentlyInRemoteHris: "Current remote data",
        diff: "Remote data changes since last sync",
      };

      const rawDataTabQueryTabRenderFns: QueryTabRenderFns<RawDataTabKey> = {
        fromLastSync: () => (
          <div className="flex-1">
            <Text
              variant="compact-title"
              className="mb-2 text-gray-7"
              multiline
            >
              Data from the remote HRIS system at the time of the last sync.
            </Text>
            <div className="mt-4" />
            <FormattedJson data={employee.rawHrisData} />
          </div>
        ),
        currentlyInRemoteHris: () => (
          <div className="flex-1">
            <Text
              variant="compact-title"
              className="mb-2 text-gray-7"
              multiline
            >
              Data fetched directly from the remote HRIS system at the current
              time.
            </Text>
            {remoteHrisData?.HrisEmployee?.remoteHrisDataPassthrough ===
              null && (
              <DataNotAvailableMessage message="Data not available for this integration type." />
            )}
            {remoteHrisData?.HrisEmployee?.remoteHrisDataPassthrough && (
              <>
                <div className="mt-4" />
                <FormattedJson
                  data={remoteHrisData.HrisEmployee.remoteHrisDataPassthrough}
                />
              </>
            )}
            {remoteHrisDataLoading &&
              !remoteHrisData?.HrisEmployee?.remoteHrisDataPassthrough && (
                <div className="text-gray-6">Loading...</div>
              )}
            {remoteHrisDataError &&
              !remoteHrisData?.HrisEmployee?.remoteHrisDataPassthrough && (
                <div className="text-red-6">Error loading data</div>
              )}
          </div>
        ),
        diff: () => (
          <div className="flex-1">
            <Text
              variant="compact-title"
              className="mb-2 text-gray-7"
              multiline
            >
              Diff showing what has changed in the remote HRIS system since the
              last sync to Opus. These changes will be reflected in Opus the
              next time a sync is run.
            </Text>
            {remoteHrisData?.HrisEmployee?.remoteHrisDataPassthrough ===
              null && (
              <DataNotAvailableMessage message="Data not available for this integration type" />
            )}
            {remoteHrisData?.HrisEmployee?.remoteHrisDataPassthrough &&
              employee.rawHrisData && (
                <>
                  <div className="mt-4" />
                  <StyledJsonDiff
                    jsonA={employee.rawHrisData}
                    jsonB={
                      remoteHrisData.HrisEmployee?.remoteHrisDataPassthrough
                    }
                  />
                </>
              )}
            {remoteHrisDataLoading && !remoteHrisData?.HrisEmployee && (
              <div className="text-gray-6">Loading...</div>
            )}
            {remoteHrisDataError && !remoteHrisData?.HrisEmployee && (
              <div className="text-red-6">Error loading data</div>
            )}
          </div>
        ),
      };

      return (
        <div className="mt-1 flex min-h-0 flex-1 flex-col">
          <QueryTabs<RawDataTabKey>
            tabs={[...rawDataTabKeys]}
            labels={rawDataTabLabels}
            render={rawDataTabQueryTabRenderFns}
            defaultTab="fromLastSync"
            queryParamKey="hris-employee-raw-data-tab"
            contentContainerStyle={{ overflow: "hidden", paddingTop: 0 }}
          />
        </div>
      );
    },
    history: () => {
      return (
        <>
          <Text variant="compact-title" className="mb-2 text-gray-7" multiline>
            Historical snapshots of the employee's data are created any time it
            is updated, which primarily only happens during a sync from the
            external HRIS system.
          </Text>
          <div className="mt-4" />
          <PaginatedTable<
            HrisEmployeeEventFragment,
            HistoryColumn,
            typeof useHrisEmployeeEventsQuery,
            typeof useHrisEmployeeEventsLazyQuery,
            HistoryColumn
          >
            key="employee-detail-sidebar-history-table"
            defaultSort={{ column: "pghId", descending: true }}
            queryHook={useHrisEmployeeEventsQuery}
            lazyQueryHook={useHrisEmployeeEventsLazyQuery}
            getItems={(x) => x?.HrisEmployeeEvents?.objects ?? []}
            getTotalItemCount={(x) => x?.HrisEmployeeEvents?.totalCount ?? 0}
            itemId={(x) => x.pghId}
            input={historyInputFilters}
            enableSearch={false}
            sidebarConfig={{
              tableId: "hris-employee-events",
              sidebarType: DetailSidebarType.HrisEmployeeEvent,
              enabled: true,
            }}
            allColumns={[...historyColumns]}
            columnSelection={{
              enabled: true,
              alwaysVisibleColumns: ["pghId", "pghCreatedAt", "diffSummary"],
              defaultSelectedColumns: ["pghId", "pghCreatedAt", "diffSummary"],
            }}
            columnTitles={{
              pghId: "Snapshot ID",
              pghCreatedAt: "Timestamp",
              diffSummary: "Modified fields in external HRIS data",
              pghContextActorId: "Actor ID",
              pghContextActorName: "Actor Name",
              pghContextCorrelationId: "Correlation ID",
              pghContextIsHris: "Is HRIS",
              pghContextReason: "Reason",
            }}
            disabledSorts={[
              "pghCreatedAt",
              "diffSummary",
              "pghContextActorId",
              "pghContextActorName",
              "pghContextCorrelationId",
              "pghContextIsHris",
              "pghContextReason",
            ]}
            responsiveness={{
              pghId: {
                width: 100,
              },
              pghCreatedAt: {
                width: 150,
              },
            }}
            renderFns={{
              pghId: (x) => x.pghId,
              pghCreatedAt: (x) =>
                x.pghCreatedAt
                  ? format(parseISO(x.pghCreatedAt), "M/d/yyyy h:mm a")
                  : "-",
              diffSummary: (x) => {
                const diffSummary = diffSummaries.get(x.pghId);

                return (
                  <Text
                    variant="compact-description"
                    className={diffSummary ? "" : "text-gray-7"}
                  >
                    {diffSummary || "No changes"}
                  </Text>
                );
              },
              pghContextActorId: (x) =>
                x.pghContext?.actorId?.toString() || "-",
              pghContextActorName: (x) => x.pghContext?.actorName || "-",
              pghContextCorrelationId: (x) =>
                x.pghContext?.correlationId || "-",
              pghContextIsHris: (x) =>
                x.pghContext?.isHris != null
                  ? x.pghContext.isHris.toString()
                  : "-",
              pghContextReason: (x) => x.pghContext?.reason || "-",
            }}
          />
        </>
      );
    },
  };

  const items: InfoTableItem[] = [
    {
      label: "External HRIS ID",
      value: employee.remoteHrisId,
    },
    {
      label: "Opus user",
      value: employee.user?.name ? (
        <Link
          href={{
            pathname: "/users/[id]",
            query: {
              id: employee.user.id.toString(),
            },
          }}
          className={cn("flex items-center no-underline text-[unset]")}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ClickableLinkText>{employee.user.name}</ClickableLinkText>
        </Link>
      ) : (
        <Text variant="compact-description" className="text-gray-7">
          Not linked
        </Text>
      ),
    },
    {
      label: "User link method",
      value: employee.manuallyLinked ? "Manually linked" : "Auto-linked",
    },
    {
      label: "HRIS locations",
      value: (
        <CommaSeparatedLinks
          items={employee.locationMemberships.map((memb) => ({
            id: memb.location.id,
            name: memb.location.name,
          }))}
          sidebarType={DetailSidebarType.HrisLocation}
          tableId="hris-locations"
        />
      ),
    },
    {
      label: "HRIS roles",
      value: (
        <CommaSeparatedLinks
          items={employee.teamMemberships.map((memb) => ({
            id: memb.team.id,
            name: memb.team.name,
          }))}
          sidebarType={DetailSidebarType.HrisRole}
          tableId="hris-roles"
        />
      ),
    },
    {
      label: "Phone number",
      value: employee.phoneNumber,
    } as InfoTableItem,
    {
      label: "HRIS Status",
      value: (
        <Text
          variant="compact-description"
          className={
            employee.employmentStatus === "ACTIVE" ? "" : "text-gray-7"
          }
        >
          {employee.employmentStatus || "--"}
        </Text>
      ),
    } as InfoTableItem,
    {
      label: "HRIS start date",
      value: employee.startDate
        ? format(parseISO(employee.startDate), "M/d/yyyy h:mm a")
        : "--",
    } as InfoTableItem,
    {
      label: "HRIS termination date",
      value: employee.terminationDate
        ? format(parseISO(employee.terminationDate), "M/d/yyyy h:mm a")
        : "--",
    } as InfoTableItem,
    // Conditionally show HRIS Integration or SFTP Integration
    ...(hasRegularHrisIntegration
      ? [
          {
            label: "HRIS integration",
            value: employee.hrisIntegration?.id ? (
              <SidebarLink
                id={employee.hrisIntegration.id}
                sidebarType={DetailSidebarType.HrisIntegration}
                tableId="hris-integrations-table"
              >
                {employee.hrisIntegration.id}
              </SidebarLink>
            ) : (
              "--"
            ),
          } as InfoTableItem,
        ]
      : []),
    {
      label: "First synced at",
      value: format(parseISO(employee.createdAt), "M/d/yyyy h:mm a"),
    } as InfoTableItem,
    {
      label: "Last updated at",
      value: format(parseISO(employee.updatedAt), "M/d/yyyy h:mm a"),
    } as InfoTableItem,
    // Conditionally show Last modified by sync or Last modified by CSV upload
    ...(hasMergeBackfillRuns
      ? [
          {
            label: "Last modified by sync",
            value: employee.lastModifiedByMergeBackfillRunId ? (
              <SidebarLink
                id={employee.lastModifiedByMergeBackfillRunId.toString()}
                sidebarType={DetailSidebarType.HrisSync}
                tableId="hris-syncs-table"
              >
                Sync {employee.lastModifiedByMergeBackfillRunId}
              </SidebarLink>
            ) : (
              "--"
            ),
          } as InfoTableItem,
        ]
      : []),
    ...(employee.csvUpload?.id
      ? [
          {
            label: "Last modified by CSV sync",
            value: (
              <SidebarLink
                id={employee.csvUpload.id}
                sidebarType={DetailSidebarType.HrisCsvUpload}
                tableId="hris-csv-uploads"
              >
                {employee.csvUpload.id}
              </SidebarLink>
            ),
          },
          {
            label: "Last modified by CSV file",
            value: employee.csvUpload.csvUrl.split("/").pop(),
          },
        ]
      : []),
  ];

  return (
    <div className="flex h-full flex-col">
      {showHeader && <SidebarHeader />}
      <SidebarTitle
        title={`${employee.firstName} ${employee.lastName}`}
        icon={<UserOutlineIcon className="size-[18px] text-gray-9" />}
        sidebarType={DetailSidebarType.HrisEmployee}
      />
      <div className="px-4">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setLinkEmployeeOpen(true);
            }}
          >
            <LinkIcon className="text-muted-foreground" />
            {employee.user ? "Edit opus user link" : "Link to opus user"}
          </Button>
          {linkEmployeeOpen && (
            <LinkHrisEmployeeDialog
              open={linkEmployeeOpen}
              onOpenChange={setLinkEmployeeOpen}
              hrisEmployee={employee}
            />
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon-sm">
                <DotsIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="bottom">
              <DropdownMenuItem
                className="cursor-pointer"
                disabled={!userActionPermissions?.changeOrgSettings}
                onClick={() => {
                  setDeleteEmployeeOpen(true);
                }}
              >
                <TrashOutlineIcon className="text-gray-10" />
                <Text variant="compact-title">Delete</Text>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <SidebarContent>
        <div className="flex min-h-0 flex-1 flex-col">
          <QueryTabs<TabKey>
            tabs={[...tabKeys]}
            labels={tabLabels}
            render={tabQueryTabRenderFns}
            counts={tabCounts as QueryTabCounts<TabKey>}
            defaultTab="details"
            queryParamKey="hris-employee-tab"
            removeVerticalPadding={{ rawData: true }}
          />
        </div>
      </SidebarContent>
      {deleteEmployeeOpen && employee && (
        <DeleteHrisEmployeeDialog
          open={deleteEmployeeOpen}
          onOpenChange={setDeleteEmployeeOpen}
          employeeName={`${employee.firstName} ${employee.lastName}`}
          onDelete={async () => {
            await deleteHrisEmployees({
              variables: { ids: [employee.id] },
            });
          }}
        />
      )}
    </div>
  );
};

// GraphQL query for the sidebar - fetches a single HRIS employee by ID
gql`
  query HrisEmployeeSidebar($id: Int!) {
    HrisEmployee(id: $id) {
      ...HrisEmployee
      terminationDate
      manuallyLinked
      rawHrisData
      hrisIntegration {
        id
        hrisProvider
      }
      createdAt
      updatedAt
      csvUpload {
        id
        csvUrl
      }
    }
  }

  query HrisEmployeeRemoteHrisData($id: Int!) {
    HrisEmployee(id: $id) {
      id
      remoteHrisDataPassthrough
    }
  }

  fragment HrisEmployeeEvent on HrisEmployeeEvent {
    pghId
    pghCreatedAt
    pghLabel
    id
    firstName
    lastName
    phoneNumber
    employmentStatus
    remoteHrisId
    startDate
    terminationDate
    manuallyLinked
    rawHrisData
    pghContext {
      actorId
      actorName
      correlationId
      isHris
      reason
    }
  }

  query HrisEmployeeEvents(
    $input: HrisEmployeeEventsInput!
    $pagination: PaginationInput
  ) {
    HrisEmployeeEvents(input: $input, pagination: $pagination) {
      totalCount
      objects {
        ...HrisEmployeeEvent
      }
    }
  }
`;

export default HrisEmployeeSidebar;
